import { api, dismiss, getQueue, mutate } from '../api'
import { chaseMessage } from '../chase-message'
import { applySavedBusinessRow, wordMessage, type BusinessSettings } from '../business-settings'
import { finalMessage } from '../rebook-message'
import { reviewsLinkProblem } from '../reviews-link'
import { pinFoot } from '../pin-foot'
import { lineDrawing, type Drawing } from '../picture-tiles'
import { MESSAGE_BY_OPTIONS } from '../contact'
import { getMe, ownerDoesJobs, worksAlone } from '../me'
import { routeGen } from '../route-state'
import { guideSeen, markGuideSeen } from '../feature-guide'
import { clear, el, loadError, toast } from '../ui'
import { offerUndo } from '../undo'
import { featureIcon } from './extra-features'
import { ownerPayCard } from '../owner-pay-card'

// "Your business" (owner, contract §8) — the five words the app puts in a
// customer's hands: the two names on a running-late text and the Money
// screen, the two message signoffs, and the bank details a payment chase
// offers. Same module shape as src/screens/analysis.ts: mountBusiness() +
// businessOnDataChanged(). Route #/business and its nav entry are wired
// elsewhere.

interface BusinessPayload {
  settings: BusinessSettings
  version: number
}

let payload: BusinessPayload | null = null

let requestSequence = 0
let saving = false
// How you usually message customers saves on its own, the moment it is picked (Liam, 15 Oct 2026: "i changed preferred
// to text but it didnt change the messages still shows whatsappp": left for the Save at the foot of the page, a picked
// way looked done and was not). The page's Save no longer sends it. askedWay is the way last asked for on this phone,
// saved or still waiting for signal, so a change of mind is always sent after it (Codex, 15 Oct 2026); keepTyped is
// the latest page's way of keeping what is typed when a clash reloads it.
const USUAL_WAY_LABEL = 'How you usually message customers'
let askedWay: string | null = null
let keepTyped: (() => void) | null = null
const usualWayWaiting = (): boolean => getQueue().some((q) => q.method === 'PATCH' && q.path === '/api/business' && q.label === USUAL_WAY_LABEL && q.status !== 'conflict')
const wayLabel = (v: string): string => MESSAGE_BY_OPTIONS.find((o) => o.value === v)?.label ?? v
const usualSelect = (): HTMLSelectElement | null => document.querySelector<HTMLSelectElement>('select[data-usual-way]')

// A save of Your business that landed (here, or from the queue once signal returned: api.ts sends
// dropby:business-saved): the page's version moves on with it, so the next save from this page is not refused.
function tookSavedRow(row: unknown): void {
  const r = row as Record<string, unknown> | null
  if (!payload || !r || typeof r.version !== 'number' || r.version <= payload.version) return
  payload.version = r.version
  if (typeof r.message_by === 'string') payload.settings = { ...payload.settings, message_by: r.message_by as BusinessSettings['message_by'] }
}
if (typeof window !== 'undefined') window.addEventListener('dropby:business-saved', (e) => tookSavedRow((e as CustomEvent).detail))

// Saves the round's usual way on its own. It always uses the version of the page as it is now (a Save of the other
// boxes, or a reload, in between moves it on), so its Undo is never refused for a change made on this phone.
async function saveUsualWay(to: string, withUndo: boolean): Promise<void> {
  if (!payload) return
  const from = askedWay ?? payload.settings.message_by ?? 'whatsapp'
  if (to === from) return
  askedWay = to
  const ack = await mutate<Record<string, unknown>>({ label: USUAL_WAY_LABEL, method: 'PATCH', path: '/api/business', body: { version: payload.version, message_by: to } })
  const select = usualSelect()
  if (ack.ok && ack.row) {
    // The rest of the app reads it from memory (business-settings.ts): every send button drawn after this goes the new way.
    applySavedBusinessRow(ack.row)
    tookSavedRow(ack.row)
    if (select && askedWay === to) select.value = to
    if (withUndo) offerUndo(`Messages now open as ${wayLabel(to)}`, () => void saveUsualWay(from, false))
    else toast(`Back to ${wayLabel(to)}`)
    return
  }
  // No signal: it waits on this phone and saves when signal returns (the save chip shows it); the box keeps showing it.
  if (ack.status === 0 || ack.status >= 500) { toast('No signal. Saved on this phone to send later.'); return }
  askedWay = payload?.settings.message_by ?? null
  if (select && askedWay) select.value = askedWay
  if (ack.status === 409) {
    for (const q of getQueue()) if (q.status === 'conflict' && q.method === 'PATCH' && q.path === '/api/business' && q.label === USUAL_WAY_LABEL) dismiss(q.key)
    toast('Your business was changed on another phone or computer first. Choose again.')
    keepTyped?.()
    if (window.location.hash.startsWith('#/business')) void load()
  } else if (ack.signedOut) toast('Your sign-in has expired. Sign in again and choose again.')
  else toast(ack.error ?? "That didn't save. Choose again.")
}
// What the owner typed, kept when the save is refused because the details were changed somewhere else first (bug hunt
// 8 Oct 2026: the reload threw the typing away and its message never showed). Only the fields they changed, for the
// round they typed them on; the rest show the fresh details. Never saved again by itself: they check and tap Save.
type Field = 'business_name' | 'short_name' | 'chase_signoff' | 'booking_signoff' | 'bank_lines' | 'message_by' | 'reviews_link'
let draft: { businessId: number | null; values: Partial<Record<Field, string>> } | null = null
let notice = ''

function field(label: string, node: HTMLElement, hint?: string): HTMLElement {
  const wrap = el('label', { class: 'booking-field' }, el('span', {}, label), node)
  if (hint) wrap.append(el('p', { class: 'muted field-help' }, hint))
  return wrap
}

function render(data: BusinessPayload, root: HTMLElement): void {
  clear(root)
  const s = data.settings

  const heading = el('div', { class: 'page-heading' })
  heading.append(el('h2', {}, 'Your business'))
  root.append(heading)
  if (ownerDoesJobs()) root.append(el('p', {}, el('a', { href: '#/my-days', class: 'btn-secondary' }, 'My working days'), el('span', { class: 'muted' }, ' Set your usual week and days off.')))

  // Extra features (Liam 2 Oct 2026): the one way in to switching on what Roundly can do for this round. Its own
  // section above the form, so it reads as something new to switch on, not one more setting. The first time it is
  // shown it glows twice (feature-guide.ts).
  const extra = el(
    'a',
    { class: 'xf-banner', href: '#/extra-features' },
    featureIcon('xf-banner-icon'),
    el(
      'span',
      { class: 'xf-banner-words' },
      el('span', { class: 'xf-banner-title' }, el('strong', {}, 'Extra features'), el('span', { class: 'xf-new' }, 'New')),
      el('span', { class: 'xf-banner-line' }, 'Turn on more of what Roundly can do, like several jobs per customer.'),
    ),
    el('span', { class: 'xf-banner-chev', 'aria-hidden': 'true' }, '›'),
  )
  if (!guideSeen('business-card')) {
    extra.classList.add('xf-glow-twice')
    markGuideSeen('business-card')
  }
  root.append(el('section', { class: 'xf-banner-section', 'aria-label': 'Extra features' }, extra))

  // No "Your prices" link here on purpose (Liam, 1 Oct: "we only want to go through this onboarding if they switch
  // the feature on otherwise the app gets complicated"). #/prices opens by its address until switching on quotes
  // leads an owner there as its first step.

  // Four cards and a live preview (Liam, 26 Oct 2026: "this what your customers see in business needs a redesign and
  // maybe an area to google rveiws"; drawn https://claude.ai/artifact/XBBEu53mwPMSYAxAEvWbez, "im happy with those
  // designs"): Your business, Your name on messages, How customers pay, Google reviews, then What customers get with
  // Payment / Booking / Taking off, changing as you type. Save pinned at the foot on a phone; on a computer the preview
  // sits beside the cards.
  const form = el('form', { class: 'business-form business-cards' })
  const saved: Record<Field, string> = { business_name: s.business_name, short_name: s.short_name, chase_signoff: s.chase_signoff,
    booking_signoff: s.booking_signoff, bank_lines: s.bank_lines ?? '', message_by: s.message_by ?? 'whatsapp', reviews_link: s.reviews_link ?? '' }
  const kept = draft && draft.businessId === (getMe()?.businessId ?? null) ? draft.values : {}
  const businessName = el('input', { type: 'text', maxlength: '80', required: 'required' })
  businessName.value = kept.business_name ?? saved.business_name
  const shortName = el('input', { type: 'text', maxlength: '80', required: 'required' })
  shortName.value = kept.short_name ?? saved.short_name
  const chaseSignoff = el('input', { type: 'text', maxlength: '80', required: 'required' })
  chaseSignoff.value = kept.chase_signoff ?? saved.chase_signoff
  const bookingSignoff = el('input', { type: 'text', maxlength: '80', required: 'required' })
  bookingSignoff.value = kept.booking_signoff ?? saved.booking_signoff
  const bankLines = el('textarea', { rows: '4', maxlength: '300' })
  bankLines.value = kept.bank_lines ?? saved.bank_lines
  const reviewsLink = el('input', { type: 'text', maxlength: '300', inputmode: 'url', autocapitalize: 'off', spellcheck: 'false', autocomplete: 'off', placeholder: 'https://maps.app.goo.gl/…' }) as HTMLInputElement
  reviewsLink.value = kept.reviews_link ?? saved.reviews_link

  // How messages usually go (0036). One customer or enquiry can go another way from the arrow beside its send button.
  // It saves the moment it is picked (saveUsualWay below), so it shows the way last asked for on this phone: the saved
  // one, or one still waiting for signal.
  if (!usualWayWaiting()) askedWay = saved.message_by
  const messageBy = el('select', { 'data-usual-way': '' }) as HTMLSelectElement
  for (const o of MESSAGE_BY_OPTIONS) {
    const opt = el('option', { value: o.value }, o.label)
    if (o.value === (askedWay ?? saved.message_by)) opt.selected = true
    messageBy.append(opt)
  }
  messageBy.addEventListener('change', () => void saveUsualWay(messageBy.value, true))
  // A clash on that save reloads the page: whatever is typed in the other boxes is kept for it, as Save's own clash does.
  const typedNow = (): Partial<Record<Field, string>> => ({ business_name: businessName.value, short_name: shortName.value, chase_signoff: chaseSignoff.value,
    booking_signoff: bookingSignoff.value, bank_lines: bankLines.value, reviews_link: reviewsLink.value })
  keepTyped = () => {
    const typed = typedNow()
    const values: Partial<Record<Field, string>> = {}
    for (const k of Object.keys(typed) as Field[]) if ((typed[k] ?? '').trim() !== saved[k].trim()) values[k] = typed[k]
    draft = Object.keys(values).length ? { businessId: getMe()?.businessId ?? null, values } : null
  }

  const card = (title: string, drawing: Drawing | null, ...body: HTMLElement[]): HTMLElement =>
    el('section', { class: 'business-card' }, el('div', { class: 'business-card-head' },
      ...(drawing ? [el('span', { class: 'business-card-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 18))] : []), el('h3', {}, title)), ...body)
  const severalRounds = (getMe()?.rounds?.length ?? 0) >= 2
  // With several rounds the round name comes first: it is what tells them apart.
  const businessNameField = field('Business name', businessName, 'Introduces you on a running-late text: "it’s Sendi from …"')
  const roundNameField = field('Round name', shortName, severalRounds ? 'Only you see this, when you switch rounds and on Money' : 'Only you see this, on Money: "Kept by …"')
  const yourBusiness = card('Your business', 'house', ...(severalRounds ? [roundNameField, businessNameField] : [businessNameField, roundNameField]))
  const howMessaged = field('How you usually message customers', messageBy, 'Saved as soon as you pick it. Change one customer with the arrow beside their send button.')
  const names = card('Your name on messages', 'bubble',
    field('Payment reminders are from', chaseSignoff),
    field('Other messages are from', bookingSignoff),
    howMessaged,
    el('a', { href: '#/your-messages', class: 'business-card-link' }, 'Change your messages ›'))
  // A one-person round has no worker to hide it from (Liam, 19 Oct 2026).
  const pay = card('How customers pay', 'bank',
    field('Bank details on payment messages', bankLines, `Leave blank to leave the bank paragraph out.${worksAlone() ? '' : ' Never shown to a worker.'}`))
  const reviewsError = el('p', { class: 'business-reviews-error', 'aria-live': 'polite' })
  const openCheck = el('a', { class: 'business-card-link', target: '_blank', rel: 'noopener' }, 'Open to check ›')
  const reviews = card('Google reviews', 'star',
    field('Your Google reviews link', reviewsLink),
    el('div', { class: 'business-reviews-check' }, openCheck, el('span', { class: 'muted' }, 'Opens your page on Google')),
    reviewsError,
    el('p', { class: 'muted field-help' }, 'Goes in your final "taking you off" message and the thanks for paying. Quotes and enquiry replies: add it in Your messages.'))
  reviews.classList.add('business-card-reviews')
  const paintLink = (): void => {
    const link = reviewsLink.value.trim()
    const why = link ? reviewsLinkProblem(link) : null
    reviewsError.textContent = why ?? ''
    openCheck.hidden = !link || !!why
    if (link && !why) openCheck.href = link
  }
  reviewsLink.addEventListener('input', paintLink)
  paintLink()

  // What customers get: the message exactly as a customer would receive it, from the same builders the app sends with,
  // fed what is typed so far rather than what is saved.
  type Showing = 'payment' | 'booking' | 'takeoff'
  let showing: Showing = 'payment'
  const bubble = el('p', { class: 'business-bubble' })
  const chips = el('div', { class: 'business-preview-chips', role: 'group', 'aria-label': 'Which message' })
  const chipButtons: [Showing, HTMLElement][] = []
  const typedSettings = (): BusinessSettings => ({
    ...s,
    business_name: businessName.value.trim() || s.business_name,
    short_name: shortName.value.trim() || s.short_name,
    chase_signoff: chaseSignoff.value.trim() || s.chase_signoff,
    booking_signoff: bookingSignoff.value.trim() || s.booking_signoff,
    bank_lines: bankLines.value.trim() || null,
    reviews_link: reviewsLink.value.trim() && !reviewsLinkProblem(reviewsLink.value.trim()) ? reviewsLink.value.trim() : null,
  })
  const paintPreview = (): void => {
    const t = typedSettings()
    bubble.textContent = showing === 'payment' ? chaseMessage({ name: 'Sarah', date: '2026-03-31', amountPence: 6000 }, 'full', t)
      : showing === 'booking' ? wordMessage('rebook_due', { 'first name': 'Sarah', 'jobs are': 'windows are', 'day question': 'Would Tuesday 13th October work for you?', 'your name': t.booking_signoff }, t)
      : finalMessage('Louise', '2025-04-10', 'windows', t)
    for (const [key, b] of chipButtons) b.setAttribute('aria-pressed', String(key === showing))
  }
  for (const [key, label] of [['payment', 'Payment'], ['booking', 'Booking'], ['takeoff', 'Taking off']] as const) {
    const b = el('button', { type: 'button', class: 'business-chip', 'data-show': key }, label)
    b.addEventListener('click', () => { showing = key; paintPreview() })
    chipButtons.push([key, b])
    chips.append(b)
  }
  for (const box of [businessName, shortName, chaseSignoff, bookingSignoff, bankLines, reviewsLink]) box.addEventListener('input', paintPreview)
  const preview = el('aside', { class: 'business-card business-preview' }, el('div', { class: 'business-card-head' }, el('h3', {}, 'What customers get')),
    chips, bubble, el('span', { class: 'muted business-preview-hint' }, 'Changes as you type'))
  paintPreview()

  const error = el('p', { class: 'form-error', role: 'alert' }, notice)
  notice = ''
  const save = el('button', { type: 'submit', class: 'btn-primary business-save' }, 'Save')
  const fields = el('fieldset', { class: 'business-cards-grid' }, yourBusiness, names, pay, reviews)
  form.append(el('div', { class: 'business-cards-main' }, fields, error), preview, pinFoot(save))
  // How you and your people get paid (0097): a round with people only; a one-person round has no close-out to fill in yet.
  root.append(form, timesCard(data), bookAgainSwitch(data), ...(worksAlone() ? [] : [ownerPayCard()]))

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (saving) return
    error.textContent = ''
    if (!shortName.value.trim()) { error.textContent = 'Round name is required.'; return }
    const linkWhy = reviewsLink.value.trim() ? reviewsLinkProblem(reviewsLink.value.trim()) : null
    if (linkWhy) { error.textContent = `Google reviews link: ${linkWhy}`; reviewsLink.focus(); return }
    saving = true
    save.disabled = true
    fields.disabled = true
    const body: Record<string, unknown> = {
      version: data.version,
      business_name: businessName.value.trim(),
      short_name: shortName.value.trim(),
      chase_signoff: chaseSignoff.value.trim(),
      booking_signoff: bookingSignoff.value.trim(),
      bank_lines: bankLines.value.trim(),
      reviews_link: reviewsLink.value.trim(),
    }
    const ack = await mutate({ label: 'Save business details', method: 'PATCH', path: '/api/business', body })
    saving = false
    if (ack.ok) {
      draft = null
      // The rest of the app reads these words from memory (business-settings.ts): swap them in now, so a send
      // button drawn after this save goes the new way without a reload (Astra, 30 Sep).
      applySavedBusinessRow(ack.row)
      // Its version now, before the reload: an Undo of the usual way tapped meanwhile is not refused (Codex, 15 Oct 2026).
      tookSavedRow(ack.row)
      toast('Saved')
      void load()
      return
    }
    save.disabled = false
    fields.disabled = false
    if (ack.status === 409) {
      const typed: Record<Field, string> = { ...(typedNow() as Record<Field, string>), message_by: saved.message_by } // the way saves on its own
      const values: Partial<Record<Field, string>> = {}
      for (const k of Object.keys(typed) as Field[]) if (typed[k].trim() !== saved[k].trim()) values[k] = typed[k]
      draft = { businessId: getMe()?.businessId ?? null, values }
      notice = 'These details were changed on another phone or computer first. Your changes are still below: check them and tap Save again.'
      // Shown here, so the refused save is answered: left parked it held the next Save behind it.
      for (const q of getQueue()) if (q.status === 'conflict' && q.method === 'PATCH' && q.path === '/api/business' && q.label === 'Save business details') dismiss(q.key)
      void load()
    } else if (ack.status >= 400 && ack.status < 500) {
      error.textContent = ack.error ?? 'Check the fields and try again.'
    } else if (ack.signedOut) {
      error.textContent = 'Your sign-in has expired. Sign in again and try saving.'
    } else {
      error.textContent = 'Waiting for signal. Your change is queued and will save once the connection is back.'
    }
  })
}

// Do you give customers a set time? (Liam, 10 Oct 2026: "not all window cleaners have times they go"; drawn in the
// canvas https://claude.ai/artifact/Uw6m34cryTTjB8pq2A2RDL). Its own card with its own Save, shown only once the answer
// changes: No turns off "time agreed" on visits still to come (functions/api/business.ts), so it never saves by a tap.
function timesCard(data: BusinessPayload): HTMLElement {
  const on = data.settings.set_times !== false
  const form = el('form', { class: 'business-form business-times', 'aria-label': 'Do you give customers a set time?' })
  const option = (value: 'yes' | 'no', title: string, line: string): HTMLInputElement => {
    const input = el('input', { type: 'radio', name: 'business-set-times', value }) as HTMLInputElement
    input.checked = (value === 'yes') === on
    options.append(el('label', { class: 'times-option' }, input, el('span', { class: 'times-option-words' }, el('strong', {}, title), el('span', { class: 'muted' }, line))))
    return input
  }
  const options = el('div', { class: 'times-options' })
  const yes = option('yes', 'Yes, we agree a time', 'Visits have a time, and it can be agreed with the customer.')
  option('no', 'No, we just agree the day', "No times asked, shown or agreed. The day's order is the order you put the visits in.")
  const changes = el('div', { class: 'times-changes' }, el('span', { class: 'times-changes-head' }, 'With No'),
    ...['Booking a visit asks only for the day.',
      'Visits read "Day agreed", with no "no set time".',
      "Messages to customers say \"We'll be with you during the day.\", never a time.",
      // Not for a one-person round, which has no workers (Liam, 19 Oct 2026).
      ...(worksAlone() ? [] : ["Workers won't get job reminders: there is no time to remind them before."]),
      'Times already saved are kept, and come back if you switch to Yes.'].map((t) => el('span', {}, t)))
  const warn = el('p', { class: 'muted field-help' })
  const error = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save') as HTMLButtonElement
  const paint = (): void => {
    const changed = yes.checked !== on
    save.hidden = !changed
    warn.textContent = changed && on ? 'Saving No also turns off "time agreed" on visits still to come.' : ''
  }
  form.addEventListener('change', paint)
  form.append(el('div', { class: 'business-form-head' }, el('h3', {}, 'Do you give customers a set time?')), options, changes,
    el('div', { class: 'times-save' }, warn, error, save))
  paint()
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (saving || yes.checked === on) return
    saving = true
    save.disabled = true
    error.textContent = ''
    const ack = await mutate({ label: 'Save set times', method: 'PATCH', path: '/api/business', body: { version: data.version, set_times: yes.checked } })
    saving = false
    save.disabled = false
    if (ack.ok) {
      applySavedBusinessRow(ack.row)
      toast(yes.checked ? 'Saved: visits have a time again' : 'Saved: you just agree the day')
      void load()
    } else if (ack.status === 409) {
      for (const q of getQueue()) if (q.status === 'conflict' && q.method === 'PATCH' && q.path === '/api/business' && q.label === 'Save set times') dismiss(q.key)
      notice = 'Your business was changed on another phone or computer first. Choose again and tap Save.'
      void load()
    } else if (ack.status >= 400 && ack.status < 500) {
      error.textContent = ack.error ?? 'That could not be saved. Try again.'
    } else if (ack.signedOut) {
      error.textContent = 'Your sign-in has expired. Sign in again and try saving.'
    } else {
      error.textContent = 'Waiting for signal. Your choice is queued and will save once the connection is back.'
    }
  })
  return form
}

// Book again (0094; docs/plans/book-again/BUILD-SPEC.md FINAL 2): "Ask new customers to book again", off for every round
// until the owner turns it on. On counts from that moment: only customers whose first clean is marked done from then get
// a check, so turning it on never fills Messages with customers cleaned before. Its own card and Save, like set times.
function bookAgainSwitch(data: BusinessPayload): HTMLElement {
  const on = data.settings.book_again === true
  const form = el('form', { class: 'business-form business-times business-book-again', 'aria-label': 'Ask new customers to book again' })
  const options = el('div', { class: 'times-options' })
  const option = (value: 'on' | 'off', title: string, line: string): HTMLInputElement => {
    const input = el('input', { type: 'radio', name: 'business-book-again', value }) as HTMLInputElement
    input.checked = (value === 'on') === on
    options.append(el('label', { class: 'times-option' }, input, el('span', { class: 'times-option-words' }, el('strong', {}, title), el('span', { class: 'muted' }, line))))
    return input
  }
  const yes = option('on', 'On', "After a new customer's first clean, Messages asks you once to check on WhatsApp how often they'd like cleaning, with the price for each.")
  option('off', 'Off', 'Nothing is asked.')
  const notes = el('div', { class: 'times-changes' }, el('span', { class: 'times-changes-head' }, 'How it works'),
    ...['Only customers whose first clean is marked done after you turn it on. Nobody cleaned before.',
      'It waits until their payment message has gone from Owed, or they have paid.',
      'You send it yourself from WhatsApp, then tap their answer in Roundly.',
      'Each customer is asked once, ever.'].map((t) => el('span', {}, t)))
  const error = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save') as HTMLButtonElement
  const paint = (): void => { save.hidden = yes.checked === on }
  form.addEventListener('change', paint)
  form.append(el('div', { class: 'business-form-head' }, el('h3', {}, 'Ask new customers to book again')), options, notes, el('div', { class: 'times-save' }, error, save))
  paint()
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (saving || yes.checked === on) return
    saving = true
    save.disabled = true
    error.textContent = ''
    const ack = await mutate({ label: 'Save book again', method: 'PATCH', path: '/api/business', body: { version: data.version, book_again: yes.checked } })
    saving = false
    save.disabled = false
    if (ack.ok) {
      applySavedBusinessRow(ack.row)
      toast(yes.checked ? 'Saved: new customers get a Book again check after their first clean' : 'Saved: Book again is off')
      void load()
    } else if (ack.status === 409) {
      for (const q of getQueue()) if (q.status === 'conflict' && q.method === 'PATCH' && q.path === '/api/business' && q.label === 'Save book again') dismiss(q.key)
      notice = 'Your business was changed on another phone or computer first. Choose again and tap Save.'
      void load()
    } else if (ack.status >= 400 && ack.status < 500) {
      error.textContent = ack.error ?? 'That could not be saved. Try again.'
    } else if (ack.signedOut) {
      error.textContent = 'Your sign-in has expired. Sign in again and try saving.'
    } else {
      error.textContent = 'Waiting for signal. Your choice is queued and will save once the connection is back.'
    }
  })
  return form
}

async function load(): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    const result = await api<BusinessPayload>('/api/business')
    if (gen !== routeGen || sequence !== requestSequence) return
    payload = result
    render(payload, root)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    clear(root)
    loadError(root, (err as Error).message, () => void load())
  }
}

export function mountBusiness(): void {
  void load()
}

export function businessOnDataChanged(): void {
  // A save in flight keeps its own draft: a background refresh mid-submit
  // must not wipe the fields out from under it.
  if (!saving) void load()
}
